import type { ReactNode } from 'react';

import { AttachmentTile } from '@/components/ui/AttachmentTile';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { dailyReportsApi } from '@/features/teacher/api/dailyReports.api';
import { participationBand, TEACHING_METHOD_LABEL } from '@/features/teacher/dailyReport/constants';
import { formatDateTime } from '@/lib/format';
import type { SchoolDailyReport } from '@/types/dailyReport';

const NOT_RECORDED = 'Not recorded';

interface DetailProps {
  label: string;
  children: ReactNode;
}

function Detail({ label, children }: DetailProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="lbl">{label}</dt>
      <dd className="whitespace-pre-wrap break-words text-sm text-ink">{children}</dd>
    </div>
  );
}

export interface SchoolDailyReportModalProps {
  /** Null keeps the dialog closed. */
  report: SchoolDailyReport | null;
  onClose: () => void;
}

/** One report in full, as the teacher wrote it. Read only: there is nothing to save. */
export function SchoolDailyReportModal({ report, onClose }: SchoolDailyReportModalProps) {
  const band = participationBand(report?.participationPercent ?? null);

  return (
    <Modal
      isOpen={report !== null}
      onClose={onClose}
      size="lg"
      title={report ? `${report.displayName} · ${report.subjectName}` : ''}
      description={report ? `By ${report.savedBy} · saved ${formatDateTime(report.savedAt)}` : undefined}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {report && (
        <dl className="flex flex-col gap-5">
          <Detail label="Topic covered">{report.content}</Detail>
          <Detail label="Homework given">{report.homeworkGiven ?? 'None set'}</Detail>
          <Detail label="Remarks">{report.remarks ?? NOT_RECORDED}</Detail>
          <Detail label="Resources used">{report.resourcesUsed ?? NOT_RECORDED}</Detail>

          <div className="grid gap-5 sm:grid-cols-2">
            <Detail label="Teaching methods">
              {report.methods.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {report.methods.map((method) => (
                    <Badge key={method}>{TEACHING_METHOD_LABEL[method]}</Badge>
                  ))}
                </span>
              ) : (
                NOT_RECORDED
              )}
            </Detail>
            <Detail label="Participation">
              {band ? <Badge tone={band.tone}>{band.label}</Badge> : NOT_RECORDED}
            </Detail>
          </div>

          <Detail label="Shared with parents">{report.sharedWithParents ? 'Yes' : 'No'}</Detail>

          <Detail label="Attachments">
            {report.attachments.length > 0 ? (
              // Room above for the tile's corner, as on the teacher's form.
              <span className="flex flex-wrap gap-4 pt-2">
                {report.attachments.map((file) => (
                  <AttachmentTile
                    key={file.uuid}
                    name={file.fileName}
                    size={file.fileSize}
                    type={file.mimeType}
                    path={dailyReportsApi.attachmentUrl(file.uuid)}
                  />
                ))}
              </span>
            ) : (
              'Nothing attached'
            )}
          </Detail>
        </dl>
      )}
    </Modal>
  );
}
